import {Component, inject, ViewChild} from '@angular/core';
import {EagleILayer} from '../../classes/eagle-i-layer';
import {LeafletNomSource} from '../../sources/leaflet-nom-source';
import {LayerService} from '../../services/layer.service';
import {LeafletNomFilter} from '../../filters/leaflet-nom-filter';
import {GenOutageAggregationLevel} from '../../../../../../generated/serverModels/GenOutageAggregationLevel';
import {tap} from 'rxjs/operators';
import {HistoricalOutageData} from '../../classes/historical-outage-data';
import {ApplicationConfig} from '../../../../classes/application-config';
import {MapService} from '../../../map/services/map.service';
import {ResizeService} from '../../../../../shared/services/resize.service';
import {DecimalPipe} from '@angular/common';
import {IResizerData} from '../../../map/interfaces/resizer-data.interface';
import {NomLegendComponent} from '../../../../../shared/components/nom-legend/nom-legend.component';
import {LegendElement} from '../../classes/legend-element';
import {LeafletLayerLegend} from '../../classes/leaflet-layer-legend';
import {HttpInterceptorService} from '../../../../services/http-interceptor.service';
import {State} from '../../../outage/classes/state';
import {MatSelectChange} from '@angular/material/select';
import {Router} from '@angular/router';
import {IReportNavigationConfig} from '../../../../interfaces/report-navigation-config.interface';
import {ReportFilter} from '../../../report/enums/report-filter.enum';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';

@Component({
    selector: 'eaglei-leaflet-nom-layer',
    templateUrl: './leaflet-nom-layer.component.html',
    styleUrls: ['./leaflet-nom-layer.component.scss'],
    standalone: false,
})
export class LeafletNomLayerComponent extends EagleILayer<LeafletNomSource> {
    private router = inject(Router);

    constructor(
        private layerService: LayerService,
        // private dataService: DataService,
        private mapService: MapService,
        private resizeService: ResizeService // private chartDataService: ChartDataService
    ) {
        super();
        this.filters.runStartTime = ApplicationConfig.roundMinute();

        this.getUserPreferences();
    }

    @ViewChild(NomLegendComponent) nomLegendComponent: NomLegendComponent;

    private readonly chartInterceptorKey = 'nomLayerChartCall';

    public readonly aggregationLevels = GenOutageAggregationLevel.values().filter(
        (l) => ![GenOutageAggregationLevel.fema, GenOutageAggregationLevel.utility].includes(l)
    );
    public filters: LeafletNomFilter = new LeafletNomFilter();
    public filtersExpanded: boolean;
    public selectedStates: State[];

    public currentResizerData: IResizerData<HistoricalOutageData> = {
        tableData: [],
        columnDefs: [],
        chartConfig: undefined,
    };

    private numberPipe = new DecimalPipe('en-us');
    // private percentPipe = new PercentPipe('en-us');
    // private momentPipe = new MomentDatePipe();
    // private femaPipe = new NumeralPipe();

    protected readonly GenOutageAggregationLevel = GenOutageAggregationLevel;

    afterViewInit() {}

    private getLegend(): void {
        if (this.legend.length === 0) {
            const ele = new LegendElement();
            const legend = new LeafletLayerLegend(this.mapLayer.id, this.mapLayer.displayName);
            ele.html = this.nomLegendComponent.ele.nativeElement.innerHTML;
            legend.elements = [ele];
            this.legend = [legend];
        }
    }

    public toggleLayer(activeState: boolean, zoomToFeatures: boolean = false): void {
        if (activeState) {
            if (!this.layerSource) {
                this.layerSource = new LeafletNomSource(this.mapLayer);
                this.layerSource.updateLocationFilter(this.selectedStates);
                // we always want to zoom on first load.
                zoomToFeatures = true;
            }

            this.getOutageData(zoomToFeatures);
        } else {
            HttpInterceptorService.clearInterceptor(this.mapLayer.uiHandle);
            HttpInterceptorService.clearInterceptor(this.chartInterceptorKey);
            // This will need to be updated to only close when NOM is active in the resizer
            if (this.resizeService.resizeElementOpen.getValue()) {
                this.resizeService.closeResizeElement.next(undefined);
            }
            this.layerSource.removeFromMap();
        }
    }

    // API Calls
    private getOutageData(zoomToFeatures: boolean = true): void {
        this.mapLayer.startLoading();

        this.filters.runStartTime = this.mapService.mapDateRange.getValue().endDate;

        this.layerSource.fetchNomTiles(this.filters.aggregationLevel, this.mapService.mapDateRange.getValue().endDate);
        this.resizeService.closeResizeElement.next(undefined);
    }

    // Aggregation Level Methods
    public updateAggregation(change: MatSelectChange) {
        this.filters.aggregationLevel = change.value;

        this.layerSource.removeFromMap();
        this.getOutageData(false);
    }

    private updateLocationFilter(zoomToFeatures: boolean = true): void {
        this.mapService.nomActiveStates.next(this.selectedStates);

        this.layerSource.updateLocationFilter(this.selectedStates, zoomToFeatures);

        this.mapService.nomLoadedData.next(this.layerSource.getFeatures());
    }

    // User Preference Methods
    private getUserPreferences(): void {
        const preferences = ApplicationConfig.currentUserPreferences.getValue();
        this.filters.aggregationLevel = preferences.getOutageAggregationLevel();
        this.selectedStates = preferences.getStates();
    }

    public goToReport() {
        const filters: IReportNavigationConfig[] = [
            {type: ReportFilter.STATE, value: this.selectedStates},
            {type: ReportFilter.AGGREGATION, value: this.filters.aggregationLevel.toString()},
            // {type: ReportFilter.START_DATE, value: this.mapService.mapDateRange.value.startDate.format()},
            {type: ReportFilter.END_DATE, value: this.mapService.mapDateRange.value.endDate.format()},
        ];
        this.router.navigate([`app/reports/outages`], {state: {filters}});
    }

    public useMobileLayout(): boolean {
        return ApplicationConfig.useMobileLayout();
    }

    public onLite(): boolean {
        return ApplicationConfig.onLite();
    }

    public filterStates(states: State[]) {
        this.selectedStates = states;
        this.updateLocationFilter();
    }

    public getCountyCustomerText(info: any): string {
        return !!info.totalCustomers || !!info.total ? `(${info.modelCount > 0 ? 'Modeled' : 'Collected'})` : '';
    }
}
